import { TooltipLink } from "@/v2-backoffice-client/components/link"
import {
  getDriftResolveHref,
  REMEDIATION_RENAME_TOKEN,
  type LayerDriftRemediation,
} from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"

export type LayerDriftResolveActionsProps = {
  remediation: LayerDriftRemediation
}

/**
 * The "so do something about it" strip under a drift entry's diff.
 *
 * Drift used to end at advice: the card explained that a field had been
 * renamed and that the layer's Configuration referenced the old name, and
 * left the operator to switch tabs and redo the find-and-replace by hand.
 * The backend already knew the token, the replacement and which documents
 * mentioned it, so this turns each of those into an action.
 *
 * One action per referencing document rather than a single "fix it": a
 * field named in both Configuration and Table needs two edits, both
 * reviewed, and hiding that behind one button would either apply an edit
 * the operator never saw or quietly fix half the problem.
 */
function LayerDriftResolveActions(props: LayerDriftResolveActionsProps) {
  const { remediation } = props

  const documents = remediation.Documents
  const fromToken = remediation.FromToken
  if (documents.length === 0 || !fromToken) return null

  const renameTo =
    remediation.Kind === REMEDIATION_RENAME_TOKEN ? remediation.ToToken : null

  return (
    <footer className="layer-drift-entry-actions">
      <span className="layer-drift-entry-actions-label">
        {renameTo ? (
          <>
            Rename <code>{fromToken}</code> to <code>{renameTo}</code> in
          </>
        ) : (
          <>
            Review references to <code>{fromToken}</code> in
          </>
        )}
      </span>

      {documents.map((document) => (
        <TooltipLink
          key={document}
          to={getDriftResolveHref(remediation, document)}
          variant="secondary"
          size="xs"
          iconSrc={renameTo ? "pen-swirl" : "search"}
          data-feature-code="UI_BackofficeLayers_Drift_Resolve"
          content={
            renameTo
              ? `Open ${document} with the rename applied as an unsaved draft — review it on the map, then save`
              : `Open ${document} and jump to where ${fromToken} is referenced`
          }
        >
          {document}
        </TooltipLink>
      ))}
    </footer>
  )
}

export default LayerDriftResolveActions
